import { Badge, ViewPlaceholder } from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import {
  CalendarEventIcon,
  CircleDollarIcon,
  ClipboardTextIcon,
  FileCheckIcon,
  HashtagIcon,
  HomeIcon,
  LoaderIcon,
  Lock2Icon,
  OfficeIcon,
  UserIcon,
} from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import {
  type Maybe,
  day,
  ensure,
  formatCurrency,
  hasValue,
  sum,
} from '@vrplatform/utils';
import {
  Anchor,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { type ComponentProps, useMemo, useState } from 'react';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import {
  DrawerCollapsableTable,
  DrawerHeader,
  DrawerInfoCard,
  DrawerInfoCardLockBadges,
  DrawerInfoCardOwnerStatementsLinks,
} from '../_components';
import { getDrawerInfoCardLockReasonBadges } from '../_components/DrawerInfoCardLockBadges.utils';
import { InfoCardReconciliationItem } from '../deposit-drawer/DepositInfoCard';
import { useOwnerDetailDrawer } from '../owner-drawers';
import { PayoutBankAccountSelect } from './PayoutBankAccountSelect';
import { PayoutEllipsisMenuItems } from './PayoutEllipsisMenuItems';
import { PayoutEllipsisMenuModals } from './PayoutEllipsisMenuModals';
import { usePayoutDetailDrawer } from './usePayoutDetailDrawer';
import { DepositExpenseTransferIssues } from '../expense-drawer/DepositExpenseTransferIssues';

export const STORYBOOK_PAYOUT_DETAIL_QUERY_KEY = 'payout-detail';

function usePayoutQuery(id: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  const query = useApiQuery(
    async ($api, signal, { id }) => {
      if (!id) return null;

      const payout = await $api.GET('/transactions/{id}', {
        params: {
          path: {
            id,
          },
        },
        signal,
      });

      if (payout.error) throw payout.error;

      const attachedToOwnerStatementIds = payout.data.lines
        .map((line) => line.ownerStatementId)
        .filter(hasValue);

      if (payout.data.payment.bankRecordIds.length) {
        const bankRecord = await $api.GET('/bank-records/{id}', {
          params: {
            path: {
              id: payout.data.payment.bankRecordIds[0],
            },
          },
          signal,
        });

        if (bankRecord.error) throw bankRecord.error;

        return {
          ...payout.data,
          bankRecord: bankRecord.data,
          isReconciled: !!bankRecord.data,
          attachedToOwnerStatementIds,
          isPublished: !!attachedToOwnerStatementIds.length,
        };
      }

      return {
        ...payout.data,
        bankRecord: undefined,
        isReconciled: false,
        attachedToOwnerStatementIds,
        isPublished: !!attachedToOwnerStatementIds.length,
      };
    },
    {
      queryGroup: 'owner-statements',
      uniqueQueryKey: 'payout-detail',
      variables: {
        id,
        teamId,
      },
      skip: !id,
      queryKeys: ['payouts'],
      storybookQueryKey: STORYBOOK_PAYOUT_DETAIL_QUERY_KEY,
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

type Payout = NonNullable<ReturnType<typeof usePayoutQuery>['data']>;

export const PayoutDetailDrawer = () => {
  const { opened, close, payoutId } = usePayoutDetailDrawer();
  const { isLoading, data: payout } = usePayoutQuery(payoutId);

  const [modal, setModal] = useState<'delete' | 'edit' | null>(null);

  return (
    <>
      <PayoutEllipsisMenuModals
        payout={payout}
        deleteModal={{
          opened: modal === 'delete' && !!payout,
          closeModal: () => setModal(null),
        }}
        editModal={{
          opened: modal === 'edit' && !!payout,
          closeModal: () => setModal(null),
        }}
      />
      <Drawer opened={opened} onClose={close} size={550}>
        <DrawerHeader
          closeDrawer={close}
          loading={isLoading}
          type="Payout"
          title={
            payout && (
              <Group wrap="nowrap" gap="xs" mt={rem(5)}>
                <Box>
                  <Title order={4} fw={500} component="p" m={0}>
                    {payout.description}
                  </Title>
                </Box>
              </Group>
            )
          }
          menuItems={
            payout && (
              <PayoutEllipsisMenuItems
                openDeleteModal={() => setModal('delete')}
                openEditModal={() => setModal('edit')}
                payout={payout}
              />
            )
          }
        />
        {!payout && !isLoading ? (
          <ViewPlaceholder
            title="No payout found"
            description="Has the payout been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          <Content payout={payout} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({
  payout,
  isLoading,
}: {
  payout: Maybe<Payout>;
  isLoading: boolean;
}) => {
  if (!payout) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <DepositExpenseTransferIssues issues={payout.issues} type="payout" />

      <InfoCard payout={payout} />

      <Lines lines={payout.lines} currency={payout.currency} />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

type PayoutLine = NonNullable<Payout['lines']>[number];

const InfoCard = ({ payout }: { payout: Payout }) => {
  const { open: openOwner } = useOwnerDetailDrawer();
  const { open: openListing } = useListingDetailDrawer();
  const lockReasonBadges = useMemo(
    () =>
      getDrawerInfoCardLockReasonBadges({
        sources: [
          {
            isLocked: payout.lock?.status === 'locked',
            lock: payout.lock,
          },
        ],
        fallbackBadge: {
          label: 'Locked',
          tooltip: 'This payout is locked and cannot be edited.',
        },
      }),
    [payout.lock]
  );

  const rows = useMemo(
    () =>
      ensure<
        (ComponentProps<typeof DrawerInfoCard>['rows'][number] | undefined)[]
      >([
        lockReasonBadges.length
          ? {
              icon: Lock2Icon,
              title: 'Locked due to',
              text: <DrawerInfoCardLockBadges badges={lockReasonBadges} />,
            }
          : undefined,
        {
          icon: LoaderIcon,
          title: 'Status',
          text:
            payout.status !== 'inactive' ? null : (
              <Badge color="orange" size="md">
                Archived
              </Badge>
            ),
        },
        {
          icon: CircleDollarIcon,
          title: 'Amount',
          text: formatCurrency(payout.amount / 100, payout.currency),
        },
        {
          icon: CalendarEventIcon,
          title: 'Date',
          text: day(payout.date).format('MMM D, YYYY'),
        },
        {
          icon: OfficeIcon,
          title: 'Bank account',
          text: payout.payment.bankRecordIds.length ? (
            payout.account?.name
          ) : (
            <PayoutBankAccountSelect
              payoutId={payout.id}
              bankAccount={
                payout.account
                  ? {
                      accountId: payout.account.id,
                      title: payout.account?.name ?? '-',
                    }
                  : null
              }
            />
          ),
        },
        {
          icon: CalendarEventIcon,
          title: 'Paid At',
          text: payout.payment.date
            ? day(payout.payment.date).format('MMM D, YYYY')
            : null,
        },
        {
          icon: UserIcon,
          title: 'Contact',
          text: payout.contact ? (
            <Anchor
              component="button"
              ta="left"
              onClick={() => openOwner(payout.contact!.id, 'overview', 'push')}
            >
              {payout.contact.name}
            </Anchor>
          ) : null,
        },
        {
          icon: HomeIcon,
          title: 'Listing',
          text: payout.lines[0].listing ? (
            <Anchor
              component="button"
              ta="left"
              onClick={() =>
                openListing(payout.lines[0].listing!.id, 'overview', 'push')
              }
            >
              {payout.lines[0].listing.name}
            </Anchor>
          ) : null,
        },
        {
          icon: HashtagIcon,
          title: 'Reference',
          text: payout.uniqueRef,
        },
        {
          icon: FileCheckIcon,
          title: 'Reconciliation',
          text: (
            <InfoCardReconciliationItem
              bankRecordIds={
                payout.bankRecord?.id ? [payout.bankRecord.id] : undefined
              }
            />
          ),
        },
        {
          icon: ClipboardTextIcon,
          title: 'Statement Paid',
          text: payout.attachedToOwnerStatementIds.length && (
            <DrawerInfoCardOwnerStatementsLinks
              ids={payout.attachedToOwnerStatementIds}
            />
          ),
        },
      ]).filter(hasValue),
    [
      payout.status,
      payout.amount,
      payout.currency,
      payout.date,
      payout.contact,
      payout.id,
      payout.lines[0].listing,
      payout.account,
      payout.payment?.date,
      payout.payment?.bankRecordIds.length,
      payout.uniqueRef,
      payout.bankRecord?.id,
      payout.attachedToOwnerStatementIds,
      lockReasonBadges,
      openOwner,
      openListing,
    ]
  );

  return <DrawerInfoCard rows={rows} labelWidth={150} />;
};

const Lines = ({
  lines,
  currency,
}: {
  lines: PayoutLine[];
  currency: string;
}) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const total = useMemo(
    () => formatCurrency(sum(lines, (x) => x.amount) / 100, currency),
    [currency]
  );

  const columns = useMemo<MRT_ColumnDef<PayoutLine>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
        Cell: ({ row }) => {
          return (
            <Box>
              <Text>{row.original.description}</Text>
              <Text size="xs">{row.original.account?.name}</Text>
            </Box>
          );
        },
      },
      {
        header: 'Split',
        accessorKey: 'centTotal',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          sx: (theme) => ({
            paddingRight: theme.spacing.xs,
            fontWeight: 'bold',
          }),
        },
        size: 100,
        Cell: ({ row }) => {
          return formatCurrency(row.original.amount / 100, currency);
        },
        Footer: !lines.length ? undefined : () => total,
      },
    ],
    [currency, lines.length, total]
  );

  return (
    <DrawerCollapsableTable
      title="Lines"
      rightSection={null}
      rowData={lines}
      columns={columns}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No lines found
          </Text>
        </Center>
      )}
    />
  );
};
